import React from 'react';
import { reverse, sort } from 'ramda';
import Highlighter from 'react-highlight-words';
import { FormattedMessage } from 'react-intl';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { IQuestionText } from '../../../../common/models/question/IQuestionText';
import { IQuestionWithOwner } from '../../../../common/models/question/IQuestionWithOwner';
import { Column } from '../../../../common/models/table/Column';
import Icon from '../../../../components/Icon';
import IconButton from '../../../../components/IconButton';
import RegularButton from '../../../../components/RegularButton';
import Tooltip from '../../../../components/Tooltip';
import UserLogo from '../../../../components/UserLogo';

export const getColumns = (
  search: string,
  userId: string,
  actions: { onDelete: IBindingCallback1<string>; onEdit: IBindingCallback1<IQuestionText> }
): Column[] => [
  {
    accessor: 'text',
    Cell: ({ cell: { value } }) => (
      <Highlighter
        highlightClassName="highlighted-text"
        searchWords={search.toLowerCase().split(' ')}
        textToHighlight={value}
        className="overflow-wrap"
        autoEscape
      />
    ),
    width: 500,
    defaultCanSort: false,
    disableSortBy: true
  },
  {
    Header: () => <FormattedMessage id="author" defaultMessage="Author" />,
    id: 'author',
    Cell: api => {
      const rowEditData = api.getRowEditData();
      const { createdByUser, id } = api.row.original as IQuestionWithOwner;
      return (rowEditData?.index === api.row.index
        ? (
          <div className="bttns-container">
            <RegularButton
              size="sm"
              variant="tertiary"
              textIntlId="delete"
              defaultMessage="Delete"
              onClick={e => { actions.onDelete(id); e.stopPropagation(); }}
            />
            <RegularButton
              size="sm"
              variant="tertiary"
              textIntlId="cancel"
              defaultMessage="Cancel"
              onClick={e => { api.cancelRowEdit(); e.stopPropagation(); }}
            />
            <RegularButton
              size="sm"
              textIntlId="save"
              defaultMessage="Save"
              onClick={e => { actions.onEdit({ id, text: rowEditData?.values.text.trim() }); e.stopPropagation(); }}
            />
          </div>
        )
        : (
          <div className="d-flex align-items-center">
            <div className="table-center" style={{ width: '45px' }}>
              {createdByUser
                ? <UserLogo user={createdByUser} />
                : (
                  <Tooltip content={<FormattedMessage id="KodiakHR" defaultMessage="KodiakHR" />}>
                    <Icon icon="logo" />
                  </Tooltip>
                )}
            </div>
            {createdByUser?.id === userId && (
              <div className="table-action ml-4">
                <IconButton
                  icon={<Icon icon="pencil" className="table-action" />}
                  onClick={e => { api.editRow(api.row); e.stopPropagation(); }}
                  variant="blue"
                  tooltip={{ id: 'freeResponse.edit', defaultMessage: 'Edit question' }}
                />
              </div>
            )}
          </div>
        )
      );
    },
    editable: false,
    width: 120
  }
];

export const orderQuestions = (desc: boolean, questions: IQuestionWithOwner[], userId: string) => {
  const orderedAsc = sort((a, b) => {
    if (a.createdByUser?.id === userId || !b.createdByUser || b.createdByUser.isSupport) return 1;
    if (b.createdByUser?.id === userId || !a.createdByUser || a.createdByUser.isSupport) return -1;

    const firstUserName = `${a.createdByUser.firstName} ${a.createdByUser.lastName}`;
    const secondUserName = `${b.createdByUser.firstName} ${b.createdByUser.lastName}`;
    return firstUserName.localeCompare(secondUserName);
  }, questions);
  return desc ? reverse(orderedAsc) : orderedAsc;
};
